import { useState, useCallback, useEffect, useMemo } from 'react'
import { X, Loader2 } from 'lucide-react'
import { fetchAndCacheFileInfo } from '@papan/files/client/queries'
import { useQueryClient } from '@tanstack/react-query'
import { createPortal } from 'react-dom'
import { useTheme, getThemeClass } from '@papan/core-ui/ui'
import { getActiveTenantId } from '@papan/api-client'
import type { WorkspaceLink } from '@papan/workspace/types'
import { getRootFolderId, isRootFolderId } from './driveRoot'
import { PickerNavigator, type PickerRoot, type PickerSelection } from '@papan/files/ui/picker'

interface FilePickerDialogProps {
  /** Workspace folder ID (for workspace files section) */
  folderId: string | null
  /** Names the breadcrumb root after the container you are in — the same
   *  label WorkspaceExplorer puts on its root section. */
  entityKind?: 'project' | 'miniapp'
  /** Linked folders in the workspace */
  links: WorkspaceLink[]
  onSelect: (fileId: string) => void
  onClose: () => void
  /** Only allow selecting folders (for relink dialog) */
  foldersOnly?: boolean
  /** Subtitle hint (e.g. original path for relinking) */
  subtitle?: string
}

/**
 * Pick a file (or, in `foldersOnly` mode, a folder) from the workspace.
 * Single-column drilldown (issue #21): the workspace folder and each
 * linked folder are roots; folders drill in, files are selected on click.
 * Search spans every root's permitted subtree.
 */
export function FilePickerDialog({ folderId, entityKind, links, onSelect, onClose, foldersOnly, subtitle }: FilePickerDialogProps) {
  const { settings } = useTheme()
  const themeClass = getThemeClass(settings.theme.workspace)
  const queryClient = useQueryClient()

  // Drive-root folder id: the base for `foldersOnly` mode and the fallback
  // root when `folderId` is null (the relink picker). `getActiveTenantId()`
  // is reliably set here — this dialog only ever renders inside a tenant
  // route — so the `?? ''` is a type guard, not a real code path.
  const driveRootId = getRootFolderId(getActiveTenantId() ?? '')

  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [selectedIsFolder, setSelectedIsFolder] = useState(false)
  const [linkRoots, setLinkRoots] = useState<PickerRoot[]>([])
  const [linksResolved, setLinksResolved] = useState(false)

  // Resolve display labels for linked-folder roots (alias, or fetched name).
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      const resolved = await Promise.all(links.map(async (link): Promise<PickerRoot> => {
        const isRoot = isRootFolderId(link.targetFolderId)
        let label = link.alias || link.targetFolderId
        if (link.alias) {
          label = link.alias
        } else if (isRoot) {
          label = 'papan (root)'
        } else {
          try {
            const info = await fetchAndCacheFileInfo(queryClient, link.targetFolderId)
            label = info.name
          } catch { /* keep id fallback */ }
        }
        return { id: link.targetFolderId, label, isLink: true }
      }))
      if (!cancelled) {
        setLinkRoots(resolved)
        setLinksResolved(true)
      }
    })()
    return () => { cancelled = true }
  }, [links, queryClient])

  const roots = useMemo<PickerRoot[]>(() => {
    if (foldersOnly) {
      return [{ id: driveRootId, label: 'papan' }]
    }
    return [
      // The section AS A PLACE (a breadcrumb root), so the bare noun — the
      // sidebar's "In this Project" is a header and does not read as a crumb.
      { id: folderId ?? driveRootId, label: entityKind === 'miniapp' ? 'PDApp' : 'Project' },
      ...linkRoots,
    ]
  }, [foldersOnly, folderId, entityKind, driveRootId, linkRoots])

  // The papan workspace config file is an internal detail — never pick it.
  const hideItem = useCallback((f: { name: string }) => f.name === '.workspace.json', [])

  const handleSelect = useCallback((item: PickerSelection) => {
    setSelectedId(item.id)
    setSelectedIsFolder(item.isFolder)
  }, [])

  // foldersOnly: the currently-open folder is the selection target (none while
  // a list place such as Projects is showing).
  const handleNavigate = useCallback((navFolderId: string | null) => {
    if (foldersOnly) {
      setSelectedId(navFolderId)
      setSelectedIsFolder(navFolderId !== null)
    }
  }, [foldersOnly])

  const handleConfirm = () => {
    if (selectedId && (foldersOnly ? selectedIsFolder : !selectedIsFolder)) {
      onSelect(selectedId)
    }
  }

  const confirmDisabled = !selectedId || (foldersOnly ? !selectedIsFolder : selectedIsFolder)

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      <div className={`${themeClass} ws-glass-modal ws-anim-modal-in relative w-[600px] max-w-[calc(100vw-2rem)] h-[550px] max-h-[90vh] flex flex-col`}>
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b themed-border">
          <div>
            <h3 className="text-sm font-medium themed-text">{foldersOnly ? 'Link Folder' : 'Open File'}</h3>
            {subtitle && <p className="text-[10px] themed-text-muted break-all">{subtitle}</p>}
          </div>
          <button onClick={onClose} className="themed-text-muted hover:themed-text">
            <X size={16} />
          </button>
        </div>

        {/* Navigator — wait for linked-root labels so `roots` is stable on mount */}
        {linksResolved ? (
          <PickerNavigator
            roots={roots}
            initialFolderId={foldersOnly ? driveRootId : (folderId ?? driveRootId)}
            mode={foldersOnly ? 'folder' : 'file'}
            selectedId={selectedId}
            onSelect={handleSelect}
            onConfirm={(item) => { if (!foldersOnly) onSelect(item.id) }}
            onNavigate={handleNavigate}
            hideItem={hideItem}
            className="flex-1"
          />
        ) : (
          <div className="flex-1 flex items-center justify-center min-h-[200px]">
            <Loader2 size={16} className="animate-spin themed-text-muted" />
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-end px-4 py-3 border-t themed-border gap-2">
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-sm themed-text-secondary hover:themed-text rounded transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={confirmDisabled}
            className="px-3 py-1.5 text-sm themed-btn-primary rounded transition-colors disabled:opacity-50"
          >
            {foldersOnly ? 'Link' : 'Open'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}
